const normalizeQuantity = value => {
  if (value == null || value === "") {
    return "0x0"
  }
  if (typeof value === "number") {
    return `0x${value.toString(16)}`
  }
  if (typeof value === "string" && value.startsWith("0x")) {
    return value
  }
  try {
    return `0x${BigInt(value).toString(16)}`
  } catch (_err) {
    return value
  }
}

const normalizeChainId = chainId => {
  if (chainId == null || chainId === "") {
    return ""
  }
  if (typeof chainId === "string" && chainId.startsWith("0x")) {
    return chainId
  }
  try {
    return `0x${BigInt(chainId).toString(16)}`
  } catch (_err) {
    return chainId
  }
}

const sendTransaction = async (ethereum, from, tx) => {
  return ethereum.request({
    method: "eth_sendTransaction",
    params: [
      {
        from,
        to: tx.to,
        data: tx.data || "0x",
        value: normalizeQuantity(tx.value),
      },
    ],
  })
}

/**
 * Poll eth_getTransactionReceipt until it returns a non-null receipt or
 * the timeout is exceeded.  Returns the receipt or null on timeout.
 */
const waitForReceipt = async (ethereum, txHash, timeoutMs = 120_000, intervalMs = 3_000) => {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    try {
      const receipt = await ethereum.request({
        method: "eth_getTransactionReceipt",
        params: [txHash],
      })
      if (receipt != null) {
        return receipt
      }
    } catch (_err) {
      // transient RPC error — keep polling
    }
    await new Promise(r => setTimeout(r, intervalMs))
  }
  return null
}

/**
 * Request a personal_sign signature from the wallet (EIP-191 typed message).
 * `data` is the message hex or UTF-8 string to sign; no tx is broadcast.
 */
const requestSignature = async (ethereum, from, message) => {
  // personal_sign expects hex-encoded message
  let msgHex = message
  if (typeof message === "string" && !message.startsWith("0x")) {
    msgHex =
      "0x" +
      Array.from(new TextEncoder().encode(message), b => b.toString(16).padStart(2, "0")).join("")
  }
  return ethereum.request({
    method: "personal_sign",
    params: [msgHex, from],
  })
}

// Execute a single InstructionEnvelope call (any mode).
// call.mode variants: "direct_provider_call" | "router_call" | "solana_instructions"
const executeInstruction = async (ethereum, from, call) => {
  const mode = call.mode
  switch (mode) {
    case "direct_provider_call": {
      const chainId = normalizeChainId(call.chain_id)
      if (chainId !== "") {
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId }],
        })
      }
      // Inline approval sub-tx when present (ERC-20 allowance).
      let approvalTxHash = null
      if (call.approval?.required) {
        approvalTxHash = await sendTransaction(ethereum, from, {
          to: call.approval.to,
          data: call.approval.data,
          value: call.approval.value || "0",
        })
        // Wait for approval to be mined before the main tx.
        if (approvalTxHash) {
          await waitForReceipt(ethereum, approvalTxHash)
        }
      }
      const txHash = await sendTransaction(ethereum, from, call)
      return { txHash, approvalTxHash }
    }
    // "router_call" — canonical backend name (snake_case serde tag).
    case "router_call": {
      const chainId = normalizeChainId(call.chain_id)
      if (chainId !== "") {
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId }],
        })
      }
      const txHash = await sendTransaction(ethereum, from, {
        to: call.router_address,
        data: call.data,
        value: call.value || "0",
      })
      return { txHash, approvalTxHash: null }
    }
    // Legacy alias kept for backward compatibility with any older backend payloads.
    case "pay_router_call": {
      const chainId = normalizeChainId(call.chain_id)
      if (chainId !== "") {
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId }],
        })
      }
      const txHash = await sendTransaction(ethereum, from, {
        to: call.router_address,
        data: call.data,
        value: call.value || "0",
      })
      return { txHash, approvalTxHash: null }
    }
    case "solana_instructions": {
      // SolanaWallet.js is a runtime-only dynamic import — @solana/web3.js may
      // not be installed in all environments.  Wrap in try/catch so a missing
      // Solana wallet never blocks the EVM path.
      try {
        const solanaMod = await import(/* webpackIgnore: true */ "./SolanaWallet.js")
        return solanaMod.executeSolanaInstruction(call)
      } catch (err) {
        // Solana wallet not available — surface a structured "not supported"
        // result so the caller can render a clear UI stub instead of crashing.
        const reason =
          err?.message?.includes("No Solana wallet")
            ? err.message
            : "Solana wallet support coming — please use the Phantom app to complete this step manually."
        throw Object.assign(new Error(reason), { solanaUnsupported: true })
      }
    }
    default:
      throw new Error(`Unsupported instruction mode: ${mode}`)
  }
}

// Backward-compatible single-instruction execution.
export const requestDirectProviderTransaction = async instruction => {
  const ethereum = globalThis.window?.ethereum
  if (!ethereum?.request) {
    throw new Error("No EVM wallet found")
  }

  const envelope = typeof instruction === "string" ? JSON.parse(instruction) : instruction
  if (envelope?.mode !== "direct_provider_call") {
    throw new Error("Unsupported route instruction")
  }

  const [from] = await ethereum.request({ method: "eth_requestAccounts" })
  return executeInstruction(ethereum, from, envelope)
}

/**
 * Execute a full ExecutionPlan sequentially.
 *
 * plan = { steps: [{ id, action, description, kind, status, call, check }] }
 *
 * @param {object}   plan             — the ExecutionPlan JSON from the backend
 * @param {Function} [progressCallback] — optional (stepIndex, total, stepId, action) => void
 *                                        called just before each step begins executing
 *
 * Returns { txHash: <last-tx-hash>, results: [{ stepId, action, txHash?, approvalTxHash?, skipped, error? }] }
 *
 * Behaviour:
 *  - "complete" steps are skipped.
 *  - "signature" steps call personal_sign (no broadcast); the signature is
 *    recorded but not treated as a tx hash.
 *  - "transaction" steps are executed via executeInstruction.
 *  - After each successful "transaction" step, if the step has a non-null
 *    `check` field AND the tx hash is available, we poll
 *    eth_getTransactionReceipt until confirmed before advancing.
 *  - "solana_instructions" steps surface a clear "not supported" record and
 *    do NOT block or fail the run — subsequent EVM steps continue.
 *  - Stops on the first non-Solana failure.
 */
export const executeExecutionPlan = async (plan, progressCallback) => {
  const ethereum = globalThis.window?.ethereum
  if (!ethereum?.request) {
    throw new Error("No EVM wallet found")
  }

  const envelope = typeof plan === "string" ? JSON.parse(plan) : plan
  const steps = envelope?.steps || []
  if (!steps.length) {
    throw new Error("Empty execution plan")
  }

  const [from] = await ethereum.request({ method: "eth_requestAccounts" })

  // Switch to the chain of the first non-complete transaction step upfront.
  const firstActiveTxStep = steps.find(
    s => s.status !== "complete" && s.kind !== "signature" && s.call?.chain_id
  )
  if (firstActiveTxStep?.call?.chain_id) {
    const chainId = normalizeChainId(firstActiveTxStep.call.chain_id)
    if (chainId !== "") {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId }],
      })
    }
  }

  const results = []
  let lastTxHash = ""
  const total = steps.length

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]

    if (step.status === "complete") {
      results.push({
        stepId: step.id,
        action: step.action,
        txHash: step.tx_hash || null,
        approvalTxHash: null,
        skipped: true,
        reason: "already_complete",
      })
      continue
    }

    // Notify the UI which step is now active (1-based index for display).
    if (typeof progressCallback === "function") {
      progressCallback(i, total, step.id, step.action)
    }

    if (step.kind === "signature") {
      // Request a wallet signature for the message embedded in call data.
      // We use the `data` field of the call as the message payload.
      const msgPayload = step.call?.data ?? step.call?.payload ?? ""
      try {
        const signature = await requestSignature(ethereum, from, msgPayload)
        results.push({
          stepId: step.id,
          action: step.action,
          signature,
          txHash: null,
          approvalTxHash: null,
          skipped: false,
          kind: "signature",
        })
      } catch (err) {
        results.push({
          stepId: step.id,
          action: step.action,
          txHash: null,
          approvalTxHash: null,
          skipped: false,
          kind: "signature",
          error: err.message || String(err),
        })
        // Signature failure is non-fatal if the step was purely advisory
        // (permit-style); continue unless this is the only/last step.
        if (i === steps.length - 1) break
      }
      continue
    }

    if (!step.call) {
      results.push({
        stepId: step.id,
        action: step.action,
        txHash: null,
        approvalTxHash: null,
        skipped: true,
        reason: "no_call_data",
      })
      continue
    }

    // Solana step — execute it via the injected Solana wallet (Phantom /
    // Solflare / Backpack).  If no Solana wallet is available, fall back to a
    // graceful "complete in Phantom" stub and continue the EVM path rather
    // than failing the whole plan.
    if (step.call.mode === "solana_instructions") {
      try {
        const solanaMod = await import(/* webpackIgnore: true */ "./SolanaWallet.js")
        const outcome = await solanaMod.executeSolanaInstruction(step.call)
        const sig = outcome?.txHash || ""
        lastTxHash = sig || lastTxHash
        results.push({
          stepId: step.id,
          action: step.action,
          txHash: sig,
          approvalTxHash: null,
          skipped: false,
          kind: "solana",
        })
      } catch (err) {
        const noWallet = err?.message?.includes("No Solana wallet")
        // No injected Solana wallet → graceful stub, don't block EVM steps.
        if (noWallet) {
          results.push({
            stepId: step.id,
            action: step.action,
            txHash: null,
            approvalTxHash: null,
            skipped: true,
            reason: "solana_unsupported",
            notice:
              "No Solana wallet detected — complete this step in the Phantom app using the payload below.",
            solanaPayload: step.call.payload,
          })
          continue
        }
        // A real Solana execution error (user rejected, RPC failure, …) —
        // record it and stop, consistent with EVM failure handling.
        results.push({
          stepId: step.id,
          action: step.action,
          txHash: null,
          approvalTxHash: null,
          skipped: false,
          kind: "solana",
          error: err.message || String(err),
        })
        break
      }
      // Do NOT break on a clean stub — EVM steps after a Solana step can proceed.
      continue
    }

    try {
      const outcome = await executeInstruction(ethereum, from, step.call)
      const txHash = outcome.txHash
      lastTxHash = txHash || lastTxHash

      results.push({
        stepId: step.id,
        action: step.action,
        txHash,
        approvalTxHash: outcome.approvalTxHash,
        skipped: false,
      })

      // If this step has a `check` handle and we have a tx hash, poll
      // eth_getTransactionReceipt until the transaction is mined before
      // advancing to the next step.  This prevents the next step (e.g. deposit
      // after an approve) from being sent before the prior tx is confirmed.
      if (txHash && step.check != null) {
        const receipt = await waitForReceipt(ethereum, txHash)
        if (receipt && receipt.status === "0x0") {
          // Transaction mined but reverted.
          throw new Error(`Step "${step.action}" transaction reverted on-chain (status 0x0).`)
        }
        // If receipt is null (timeout), log a warning but continue — the tx
        // may still confirm; the backend Saga will reconcile.
        if (receipt == null) {
          console.warn(
            `NativeCrypto: receipt poll timed out for step "${step.id}" (${txHash}) — advancing anyway.`
          )
        }
      }
    } catch (err) {
      const isSolanaUnsupported = err?.solanaUnsupported === true
      results.push({
        stepId: step.id,
        action: step.action,
        txHash: null,
        approvalTxHash: null,
        skipped: isSolanaUnsupported,
        reason: isSolanaUnsupported ? "solana_unsupported" : undefined,
        notice: isSolanaUnsupported ? err.message : undefined,
        error: isSolanaUnsupported ? undefined : err.message || String(err),
      })
      if (!isSolanaUnsupported) {
        // Stop on first real failure — don't execute remaining steps with broken state.
        break
      }
    }
  }

  return { txHash: lastTxHash, results }
}
